import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Ban, Loader2, RefreshCw, Search, Sparkles } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { InlineWarning, Panel, StatusBadge } from "@/components/common/Primitives";
import { api, apiError } from "@/lib/api";
import { dateTime, num } from "@/lib/format";

const STATUS_STYLE = {
  EXACT: "border-emerald-300 bg-emerald-50 text-emerald-800",
  HIGH_CONFIDENCE: "border-emerald-300 bg-emerald-50 text-emerald-800",
  ESTIMATED: "border-amber-300 bg-amber-50 text-amber-900",
  LOW_CONFIDENCE: "border-orange-300 bg-orange-50 text-orange-900",
  MANUAL_REVIEW_REQUIRED: "border-red-300 bg-red-50 text-red-800",
};
const STATUS_LABEL = {
  EXACT: "Exact",
  HIGH_CONFIDENCE: "Hoge betrouwbaarheid",
  ESTIMATED: "Geschat",
  LOW_CONFIDENCE: "Lage betrouwbaarheid",
  MANUAL_REVIEW_REQUIRED: "Handmatige controle",
};
const SOURCE_LABEL = {
  RDW_REFERENCE: "RDW open data",
  ADVERTENTIE: "Advertentie",
  MANUAL_OVERRIDE: "Handmatig",
  EU_DATASET: "EU-dataset",
  MANUFACTURER: "Fabrikant",
  SPEC_DATABASE: "Specificatiedatabase",
  WEB_SOURCE: "Webbron",
};

export default function Co2Reference() {
  const [data, setData] = useState(null);
  const [sources, setSources] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editSource, setEditSource] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (status) params.set("status", status);
      const [refs, src] = await Promise.all([
        api.get(`/co2/references?${params.toString()}`),
        api.get("/co2/sources"),
      ]);
      setData(refs.data);
      setSources(src.data.sources || []);
    } catch (e) {
      setError(apiError(e));
    }
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    setBusy(`${id}:${action}`);
    setMessage("");
    try {
      if (action === "verify") await api.post(`/co2/references/${id}/verify`);
      if (action === "enable") await api.post(`/co2/references/${id}/disable?disabled=false`);
      if (action === "disable") await api.post(`/co2/references/${id}/disable?disabled=true`);
      if (action === "refresh") {
        const { data: res } = await api.post(`/co2/references/${id}/refresh`);
        setMessage(
          res.result?.usable
            ? `Ververst: ${res.result.co2} g/km via ${res.result.source_name} (${Math.round(
                res.result.confidence
              )}%).`
            : "Geen betrouwbare bron gevonden bij het verversen."
        );
      }
      await load();
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const saveEdit = async () => {
    setBusy(`${editing}:edit`);
    try {
      await api.patch(`/co2/references/${editing}`, {
        co2: Number(editValue),
        co2_wltp: Number(editValue),
        source_name: editSource || "Handmatig gecorrigeerd",
        verified: true,
      });
      setEditing(null);
      setEditValue("");
      setEditSource("");
      await load();
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const enrichMissing = async () => {
    setBusy("enrich");
    setMessage("");
    try {
      const { data: res } = await api.post("/co2/enrich-missing");
      setMessage(
        `${res.processed} voertuigen verwerkt · ${res.with_co2} met CO₂-bron · ` +
          `${res.without_co2} zonder bron · ${res.unique_specifications} unieke specificaties ` +
          `(${res.reused} keer hergebruikt).`
      );
      await load();
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const refs = data?.references || [];

  return (
    <AppShell
      title="CO₂ Referentiedatabase"
      subtitle="Hergebruikbaar CO₂-geheugen — elke waarde heeft een bron, matchcriteria en betrouwbaarheid"
      actions={
        <button
          type="button"
          onClick={enrichMissing}
          disabled={busy === "enrich"}
          data-testid="enrich-missing-button"
          className="flex items-center gap-1.5 rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-blue-700 disabled:opacity-60"
        >
          {busy === "enrich" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          Ontbrekende CO₂ verrijken
        </button>
      }
    >
      <div className="space-y-3">
        {error && <InlineWarning tone="danger" testid="co2-error">{error}</InlineWarning>}
        {message && <InlineWarning tone="info" testid="co2-message">{message}</InlineWarning>}

        <Panel title="CO₂-bronnen" subtitle="Prioriteitsvolgorde van de verrijkingsengine" testid="co2-sources-panel">
          <div className="divide-y divide-slate-100">
            {sources.map((s) => (
              <div
                key={s.key}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                data-testid={`co2-source-${s.key}`}
              >
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-800">
                    {s.priority}. {s.label}
                  </div>
                  <div className="text-[11px] text-slate-500">{s.message || s.description}</div>
                </div>
                <StatusBadge status={s.status} label={s.status === "CONNECTED" ? "Actief" : undefined} />
              </div>
            ))}
          </div>
        </Panel>

        <Panel
          title={`Referenties (${data?.total ?? 0})`}
          subtitle="Zoeken, controleren, corrigeren of uitschakelen"
          testid="co2-references-panel"
          actions={
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1.5 h-3.5 w-3.5 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Merk, model, uitvoering…"
                  data-testid="co2-search-input"
                  className="h-7 w-48 rounded-sm border border-slate-300 pl-7 pr-2 text-[12px] outline-none focus:border-blue-500"
                />
              </div>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="co2-status-filter"
                className="h-7 rounded-sm border border-slate-300 px-2 text-[12px] outline-none focus:border-blue-500"
              >
                <option value="">Alle betrouwbaarheid</option>
                {Object.entries(STATUS_LABEL).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
          }
        >
          {refs.length === 0 ? (
            <p className="px-3 py-6 text-center text-[12px] text-slate-500" data-testid="co2-empty">
              Nog geen CO₂-referenties opgeslagen. Start een scan of gebruik "Ontbrekende CO₂ verrijken".
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Merk / model</th>
                    <th>Uitvoering</th>
                    <th>Vermogen</th>
                    <th>Motor</th>
                    <th>Jaar</th>
                    <th>CO₂</th>
                    <th>Methode</th>
                    <th>Bron</th>
                    <th>Betrouwbaarheid</th>
                    <th>Referenties</th>
                    <th>Laatst gecontroleerd</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {refs.map((r) => (
                    <tr key={r.id} data-testid={`co2-ref-${r.id}`} className={r.disabled ? "opacity-50" : ""}>
                      <td className="text-[12px] font-semibold text-slate-800">
                        {[r.make, r.model].filter(Boolean).join(" ")}
                        {r.generation ? (
                          <span className="ml-1 font-normal text-slate-500">({r.generation})</span>
                        ) : null}
                      </td>
                      <td className="max-w-[200px] truncate text-[12px]">{r.variant || "—"}</td>
                      <td className="num">{r.power_kw ? `${Math.round(r.power_kw)} kW` : "—"}</td>
                      <td className="num">{r.engine_cc ? `${num(r.engine_cc)} cc` : "—"}</td>
                      <td className="num">{r.model_year_from || "—"}</td>
                      <td className="num font-semibold">
                        {r.co2_min && r.co2_max && r.co2_min !== r.co2_max
                          ? `${num(r.co2_min)}–${num(r.co2_max)}`
                          : num(r.co2)}{" "}
                        g/km
                      </td>
                      <td className="text-[11px]">{r.measurement_method}</td>
                      <td className="text-[11px]">
                        {r.source_url ? (
                          <a
                            href={r.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 hover:underline"
                          >
                            {SOURCE_LABEL[r.source_type] || r.source_name}
                          </a>
                        ) : (
                          SOURCE_LABEL[r.source_type] || r.source_name || "—"
                        )}
                      </td>
                      <td>
                        <span className={`chip ${STATUS_STYLE[r.confidence_status] || ""}`}>
                          {STATUS_LABEL[r.confidence_status] || r.confidence_status}
                          {r.confidence ? ` · ${Math.round(r.confidence)}%` : ""}
                        </span>
                        {r.verified && (
                          <div className="mt-0.5 text-[9.5px] font-semibold uppercase text-emerald-700">
                            geverifieerd
                          </div>
                        )}
                      </td>
                      <td className="num">{r.reference_vehicle_count ?? 0}</td>
                      <td className="text-[11px] text-slate-500">
                        {dateTime(r.last_verified_at || r.source_last_checked_at || r.updated_at)}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            title="Markeer als geverifieerd"
                            data-testid={`verify-${r.id}`}
                            onClick={() => act(r.id, "verify")}
                            className="rounded-sm border border-slate-300 p-1 text-emerald-700 hover:bg-emerald-50"
                          >
                            <BadgeCheck className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Opnieuw ophalen bij de bron"
                            data-testid={`refresh-${r.id}`}
                            onClick={() => act(r.id, "refresh")}
                            className="rounded-sm border border-slate-300 p-1 text-blue-700 hover:bg-blue-50"
                          >
                            {busy === `${r.id}:refresh` ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            title={r.disabled ? "Weer inschakelen" : "Uitschakelen (onjuist record)"}
                            data-testid={`disable-${r.id}`}
                            onClick={() => act(r.id, r.disabled ? "enable" : "disable")}
                            className="rounded-sm border border-slate-300 p-1 text-red-700 hover:bg-red-50"
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            data-testid={`edit-${r.id}`}
                            onClick={() => {
                              setEditing(r.id);
                              setEditValue(String(r.co2 ?? ""));
                              setEditSource(r.source_name || "");
                            }}
                            className="rounded-sm border border-slate-300 px-1.5 py-1 text-[10.5px] font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            Corrigeer
                          </button>
                        </div>
                        {editing === r.id && (
                          <div className="mt-1 flex items-center gap-1">
                            <input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              data-testid={`edit-value-${r.id}`}
                              className="h-7 w-20 rounded-sm border border-slate-300 px-1.5 text-[11px]"
                              placeholder="g/km"
                            />
                            <input
                              value={editSource}
                              onChange={(e) => setEditSource(e.target.value)}
                              data-testid={`edit-source-${r.id}`}
                              className="h-7 w-32 rounded-sm border border-slate-300 px-1.5 text-[11px]"
                              placeholder="bron"
                            />
                            <button
                              type="button"
                              onClick={saveEdit}
                              data-testid={`save-${r.id}`}
                              className="rounded-sm bg-slate-900 px-2 py-1 text-[10.5px] font-semibold text-white"
                            >
                              Opslaan
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-t border-slate-100 px-3 py-2 text-[11px] text-slate-500">
            Hergebruikdrempel: matchkwaliteit ≥ {data?.config?.cache_match_threshold ?? 85}% ·
            hercontrole na {data?.config?.revalidate_after_days ?? 180} dagen · minimaal{" "}
            {data?.config?.min_reference_vehicles ?? 3} referentievoertuigen voor een harde waarde.
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}
